import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Network, Users, ChevronDown, ChevronRight, Building2, User } from 'lucide-react';

function OrgStructure() {
  const { API } = useContext(AppContext);
  const [structure, setStructure] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDepts, setExpandedDepts] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [structureRes, employeesRes] = await Promise.all([
        axios.get(`${API}/hr/org-structure`),
        axios.get(`${API}/hr/employees`)
      ]);
      setStructure(structureRes.data);
      setEmployees(employeesRes.data);
      
      // Auto-expand all departments
      const expanded = {};
      structureRes.data.forEach(dept => {
        expanded[dept.department] = true;
      });
      setExpandedDepts(expanded);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDept = (dept) => {
    setExpandedDepts(prev => ({
      ...prev,
      [dept]: !prev[dept]
    }));
  };

  // Group employees by department
  const employeesByDept = employees.reduce((acc, emp) => {
    const dept = emp.department || 'Unassigned';
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(emp);
    return acc;
  }, {});

  // Default org structure if none exists
  const defaultStructure = [
    { department: 'Management', head: 'Director', level: 0, color: '#1a237e' },
    { department: 'Production', head: 'Production Manager', level: 1, color: '#e65100' },
    { department: 'Raw Material', head: 'RM Manager', level: 1, color: '#2e7d32' },
    { department: 'Dispatch', head: 'Dispatch Manager', level: 1, color: '#1565c0' },
    { department: 'Stores', head: 'Stores Manager', level: 1, color: '#7b1fa2' },
    { department: 'Maintenance', head: 'Maintenance Head', level: 1, color: '#c62828' },
    { department: 'HR', head: 'HR Manager', level: 1, color: '#00838f' },
    { department: 'Accounts', head: 'Accounts Manager', level: 1, color: '#558b2f' },
    { department: 'Quality', head: 'QC Manager', level: 1, color: '#ef6c00' },
  ];

  const orgData = structure.length > 0 ? structure : defaultStructure;

  const getDeptColor = (dept) => {
    const item = orgData.find(d => d.department === dept);
    return item?.color || '#6b7280';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a237e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="org-structure-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Network className="text-[#1a237e]" size={28} />
          Organisation Structure
        </h1>
        <p className="text-gray-600 mt-1">Company hierarchy and department overview</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total Employees</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{employees.length}</p>
            </div>
            <div className="h-12 w-12 bg-[#1a237e]/10 rounded-full flex items-center justify-center">
              <Users className="text-[#1a237e]" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Departments</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{Object.keys(employeesByDept).length}</p>
            </div>
            <div className="h-12 w-12 bg-[#e65100]/10 rounded-full flex items-center justify-center">
              <Building2 className="text-[#e65100]" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Active Employees</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {employees.filter(e => e.status === 'Active').length}
              </p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
              <User className="text-green-600" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Management</p>
              <p className="text-2xl font-bold text-purple-600 mt-1">
                {employees.filter(e => e.role === 'Admin' || e.role === 'Manager').length}
              </p>
            </div>
            <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Network className="text-purple-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Org Chart Visual */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Organisational Hierarchy</h3>
        
        {/* CEO/Director Level */}
        <div className="flex justify-center mb-8">
          <div className="bg-gradient-to-br from-[#1a237e] to-[#3949ab] text-white rounded-xl p-4 shadow-lg text-center min-w-[200px]">
            <div className="h-12 w-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <User size={24} />
            </div>
            <p className="font-bold">Director / CEO</p>
            <p className="text-sm text-white/70">Top Management</p>
          </div>
        </div>

        {/* Connecting Line */}
        <div className="flex justify-center mb-4">
          <div className="w-0.5 h-8 bg-gray-300"></div>
        </div>

        {/* Department Heads Level */}
        <div className="flex flex-wrap justify-center gap-4">
          {orgData.filter(d => d.level === 1).map((dept) => (
            <div
              key={dept.department}
              className="rounded-xl border-2 p-4 text-center min-w-[150px] transition hover:shadow-md"
              style={{ borderColor: dept.color, backgroundColor: `${dept.color}10` }}
            >
              <div 
                className="h-10 w-10 rounded-full flex items-center justify-center mx-auto mb-2"
                style={{ backgroundColor: dept.color }}
              >
                <Building2 size={20} className="text-white" />
              </div>
              <p className="font-semibold text-gray-900">{dept.department}</p>
              <p className="text-xs text-gray-600">{dept.head}</p>
              <p className="text-xs mt-1" style={{ color: dept.color }}>
                {employeesByDept[dept.department]?.length || 0} members
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Department Details */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Department Details</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {orgData.filter(d => d.level === 1).map((dept) => {
            const deptEmployees = employeesByDept[dept.department] || [];
            const isExpanded = expandedDepts[dept.department];
            
            return (
              <div key={dept.department} data-testid={`dept-${dept.department}`}>
                <button
                  onClick={() => toggleDept(dept.department)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition"
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="h-10 w-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: dept.color }}
                    >
                      <Building2 size={20} className="text-white" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">{dept.department}</p>
                      <p className="text-sm text-gray-500">{dept.head}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">{deptEmployees.length} employees</span>
                    {isExpanded ? (
                      <ChevronDown className="text-gray-400" size={20} />
                    ) : (
                      <ChevronRight className="text-gray-400" size={20} />
                    )}
                  </div>
                </button>

                {/* Employee List */}
                {isExpanded && deptEmployees.length > 0 && (
                  <div className="bg-gray-50 px-4 pb-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
                      {deptEmployees.map((emp) => (
                        <div
                          key={emp.id}
                          className="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3"
                          data-testid={`emp-${emp.id}`}
                        >
                          <div 
                            className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold"
                            style={{ backgroundColor: dept.color }}
                          >
                            {emp.name?.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{emp.name}</p>
                            <p className="text-xs text-gray-500 truncate">{emp.designation || emp.role}</p>
                          </div>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            emp.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {emp.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {isExpanded && deptEmployees.length === 0 && (
                  <div className="bg-gray-50 px-4 pb-4">
                    <p className="text-sm text-gray-500 text-center py-4">No employees in this department</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Unassigned Employees */}
      {employeesByDept['Unassigned']?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-yellow-50">
            <h3 className="text-lg font-semibold text-yellow-800">Unassigned Employees</h3>
            <p className="text-sm text-yellow-600">These employees need to be assigned to a department</p>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {employeesByDept['Unassigned'].map((emp) => (
                <div
                  key={emp.id}
                  className="bg-yellow-50 rounded-lg border border-yellow-200 p-3 flex items-center gap-3"
                >
                  <div className="h-10 w-10 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold">
                    {emp.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{emp.name}</p>
                    <p className="text-xs text-gray-500 truncate">{emp.email}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrgStructure;
